import { NOTIFICATION_CATEGORY, NOTIFICATION_STATUS, type PaginatedNotificationsResponse } from '@adtrackify/at-tracking-event-types';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from '@/components/ui/helpers/toast';

import { runWhenIdle } from '@/lib/scheduling/run-when-idle';
import { useAccountState } from '@/contexts/account/account-context';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';
import { NOTIFICATION_BATCH_ACTION } from '@/services/internal-api/accounts/types/accounts-api-service-enums';
import { NOTIFICATION_QUERY_KEYS } from '../api/notification-query-keys';

const EMPTY_PAGE: PaginatedNotificationsResponse = { notifications: [] };

function clearNotificationListCaches(queryClient: QueryClient) {
  queryClient.setQueriesData<PaginatedNotificationsResponse>(
    { queryKey: NOTIFICATION_QUERY_KEYS.all },
    (old) => (old?.notifications ? EMPTY_PAGE : old)
  );
}

function removeNotificationsFromCache(queryClient: QueryClient, ids: Set<string>) {
  queryClient.setQueriesData<PaginatedNotificationsResponse>(
    { queryKey: NOTIFICATION_QUERY_KEYS.all },
    (old) => {
      if (!old?.notifications) return old;
      return {
        ...old,
        notifications: old.notifications.filter((n) => !ids.has(n.notificationId)),
      };
    }
  );
}

export function useNotifications(options?: {
  category?: NOTIFICATION_CATEGORY;
  status?: NOTIFICATION_STATUS;
  limit?: number;
  cursor?: string;
}) {
  const { currentAccount } = useAccountState();
  const accountId = currentAccount?.id;

  return useQuery({
    queryKey: [...NOTIFICATION_QUERY_KEYS.list(accountId ?? ''), options],
    queryFn: async () => {
      const response = await AccountsApiService.getNotifications(accountId ?? '', options);
      return response.data;
    },
    enabled: !!accountId,
    staleTime: 60 * 1000,
  });
}

export function useUnreadCount() {
  const { currentAccount } = useAccountState();
  const accountId = currentAccount?.id;

  // Defer initial fetch to idle time so it doesn't compete with critical bootstrap calls
  const [isReadyToFetch, setIsReadyToFetch] = useState(false);
  useEffect(() => {
    runWhenIdle(() => setIsReadyToFetch(true));
  }, []);

  return useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.unreadCount(accountId ?? ''),
    queryFn: async () => {
      const response = await AccountsApiService.getNotificationUnreadCount(accountId ?? '');
      return response.data.count;
    },
    enabled: !!accountId && isReadyToFetch,
    // Don't refetch on every page navigation — cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Background refresh every 5 minutes when tab is active
    refetchInterval: 5 * 60 * 1000,
    // Don't refetch when window regains focus or component remounts
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Pause polling when document is hidden — eliminate background tab traffic
    refetchIntervalInBackground: false,
    // Render badge with 0 instantly on first mount to avoid empty flash
    placeholderData: 0,
  });
}

export function useMarkAsRead() {
  const { currentAccount } = useAccountState();
  const accountId = currentAccount?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => {
      if (!accountId) throw new Error('Account not loaded');
      return AccountsApiService.markNotificationRead(accountId, notificationId);
    },
    onSuccess: () => {
      // Optimistically decrement the unread count by 1. Avoid touching the cache
      // when accountId is unset so we don't write to the placeholder '' key.
      if (accountId) {
        queryClient.setQueryData<number>(
          NOTIFICATION_QUERY_KEYS.unreadCount(accountId),
          (old) => Math.max(0, (old ?? 0) - 1)
        );
      }
      // Mark stale without immediate refetch — backend may have read replica lag
      // and an immediate refetch can overwrite the optimistic value with stale data.
      void queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.all,
        refetchType: 'none',
      });
    },
    onError: (_error: Error) => {
      toast.error('Failed to mark notification as read');
    },
  });
}

export function useMarkAllRead() {
  const { currentAccount } = useAccountState();
  const accountId = currentAccount?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!accountId) throw new Error('Account not loaded');
      return AccountsApiService.markAllNotificationsRead(accountId);
    },
    onSuccess: () => {
      // All notifications are now read — set the unread count to 0 directly.
      if (accountId) {
        queryClient.setQueryData<number>(
          NOTIFICATION_QUERY_KEYS.unreadCount(accountId),
          0
        );
      }
      void queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.all,
        refetchType: 'none',
      });
      toast.success('All notifications marked as read');
    },
    onError: (_error: Error) => {
      toast.error('Failed to mark all as read');
    },
  });
}

export function useDismissAll() {
  const { currentAccount } = useAccountState();
  const accountId = currentAccount?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (filters?: { category?: string; status?: string }) => {
      if (!accountId) throw new Error('Account not loaded');
      return AccountsApiService.dismissAllNotifications(accountId, filters);
    },
    onSuccess: () => {
      clearNotificationListCaches(queryClient);
      // Dismissed notifications may be read or unread; without per-item state we
      // mark the unread count stale and let it refetch naturally on next mount.
      void queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.all,
        refetchType: 'none',
      });
      toast.success('Notifications dismissed');
    },
    onError: (_error: Error) => {
      toast.error('Failed to dismiss notifications');
    },
  });
}

export function useDeleteAll() {
  const { currentAccount } = useAccountState();
  const accountId = currentAccount?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (filters?: { category?: string; status?: string }) => {
      if (!accountId) throw new Error('Account not loaded');
      return AccountsApiService.deleteAllNotifications(accountId, filters);
    },
    onSuccess: () => {
      clearNotificationListCaches(queryClient);
      // Same rationale as useDismissAll — deleted notifications can be either
      // read or unread; mark stale and refresh naturally.
      void queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.all,
        refetchType: 'none',
      });
      toast.success('Notifications deleted');
    },
    onError: (_error: Error) => {
      toast.error('Failed to delete notifications');
    },
  });
}

export function useBatchAction() {
  const { currentAccount } = useAccountState();
  const accountId = currentAccount?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ action, notificationIds }: { action: NOTIFICATION_BATCH_ACTION; notificationIds: string[] }) => {
      if (!accountId) throw new Error('Account not loaded');
      return AccountsApiService.batchNotificationAction(accountId, action, notificationIds);
    },
    onSuccess: (_data, variables) => {
      if (
        variables.action === NOTIFICATION_BATCH_ACTION.DISMISS ||
        variables.action === NOTIFICATION_BATCH_ACTION.DELETE
      ) {
        const removedIds = new Set(variables.notificationIds);
        removeNotificationsFromCache(queryClient, removedIds);
      } else if (variables.action === NOTIFICATION_BATCH_ACTION.MARK_READ && accountId) {
        // Optimistically decrement by the count of items marked. If some were
        // already read we may briefly under-count; the next refetch corrects it.
        // Cap at 0 to avoid negative values from over-decrement.
        queryClient.setQueryData<number>(
          NOTIFICATION_QUERY_KEYS.unreadCount(accountId),
          (old) => Math.max(0, (old ?? 0) - variables.notificationIds.length)
        );
      }
      void queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.all,
        refetchType: 'none',
      });
      const count = variables.notificationIds.length;
      switch (variables.action) {
        case NOTIFICATION_BATCH_ACTION.MARK_READ:
          toast.success(`${count} notification${count !== 1 ? 's' : ''} marked as read`);
          break;
        case NOTIFICATION_BATCH_ACTION.DISMISS:
          toast.success(`${count} notification${count !== 1 ? 's' : ''} dismissed`);
          break;
        case NOTIFICATION_BATCH_ACTION.DELETE:
          toast.success(`${count} notification${count !== 1 ? 's' : ''} deleted`);
          break;
      }
    },
    onError: (_error: Error) => {
      toast.error('Failed to perform action');
    },
  });
}
